import { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { LoadingIndicator, ProtectedRoute } from '@leanpay/ui'

import { useAppStore } from 'app/app-context'
import { VendorUserRole } from 'library/constants'
import { lazyWithRetry } from 'library/lazy-with-retry'

// Lazy load all route components for code splitting
const NavigationMenu = lazyWithRetry(() =>
    import('app/components/navigation-menu/navigation-menu').then((m) => ({
        default: m.NavigationMenu,
    })),
)
const About = lazyWithRetry(() => import('app/shared/about/about'))
const CheckYourLimit = lazyWithRetry(() => import('app/shared/check-your-limit/check-your-limit'))
const Company = lazyWithRetry(() => import('app/shared/company/company'))
const ContactAndSupport = lazyWithRetry(
    () => import('app/shared/contact-and-support/contact-and-support'),
)
const CreateOffer = lazyWithRetry(() => import('app/shared/create-offer/create-offer'))
const ForgotPassword = lazyWithRetry(() => import('app/shared/forgot-password/forgot-password'))
const Login = lazyWithRetry(() => import('app/shared/login/login'))
const OfferDetails = lazyWithRetry(() => import('app/shared/offers/offer-details'))
const Offers = lazyWithRetry(() => import('app/shared/offers/offers'))
const PasswordSet = lazyWithRetry(() => import('app/shared/password-set/password-set'))
const PaymentAndInvoices = lazyWithRetry(
    () => import('app/shared/payment-and-invoices/payment-and-invoices'),
)
const PaymentInvoiceDetails = lazyWithRetry(
    () => import('app/shared/payment-and-invoices/payment-invoice-details'),
)
const RemoteOffer = lazyWithRetry(() => import('app/shared/remote-offer/remote-offer'))
const TransactionDetails = lazyWithRetry(
    () => import('app/shared/transactions/transaction-details'),
)
const Transactions = lazyWithRetry(() => import('app/shared/transactions/transactions'))
const UserManagement = lazyWithRetry(() => import('app/shared/user-management/user-management'))
const VerifyEmail = lazyWithRetry(() => import('app/shared/verify-email/verify-email'))

const MainAppSLO = () => {
    const { isAuthenticated, userRoles } = useAppStore()

    // Role checks matching Angular app
    const hasAdminRole = userRoles?.includes(VendorUserRole.ROLE_ADMIN)
    const hasOfficerRole = userRoles?.includes(VendorUserRole.ROLE_OFFICER)
    const hasSuperAgentRole = userRoles?.includes(VendorUserRole.ROLE_SUPER_AGENT)

    // Transactions & Contact: ROLE_ADMIN, ROLE_OFFICER, ROLE_SUPER_AGENT (not ROLE_AGENT)
    const hasTransactionAccess = [hasAdminRole, hasOfficerRole, hasSuperAgentRole].some(Boolean)

    return (
        <Suspense fallback={<LoadingIndicator />}>
            <Routes>
                <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/password-reset" element={<PasswordSet />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route
                    element={<ProtectedRoute isAllowed={isAuthenticated} redirectPath="/login" />}
                >
                    <Route element={<NavigationMenu />}>
                        {/* Admin-only routes: ROLE_ADMIN */}
                        <Route
                            element={<ProtectedRoute isAllowed={!!hasAdminRole} redirectPath="/" />}
                        >
                            <Route path="/company" element={<Company />} />
                            <Route path="/user-management" element={<UserManagement />} />
                            <Route path="/payment-and-invoices" element={<PaymentAndInvoices />} />
                            <Route
                                path="/payment-invoice-details/:id"
                                element={<PaymentInvoiceDetails />}
                            />
                        </Route>

                        {/* Routes for ADMIN, OFFICER, SUPER_AGENT (not AGENT) */}
                        <Route
                            element={
                                <ProtectedRoute
                                    isAllowed={hasTransactionAccess}
                                    redirectPath="/create-offer"
                                />
                            }
                        >
                            <Route path="/transactions" element={<Transactions />} />
                            <Route path="/transactions/:id" element={<TransactionDetails />} />
                            <Route path="/contact-and-support" element={<ContactAndSupport />} />
                        </Route>

                        {/* Routes for all authenticated users */}
                        <Route index element={<Navigate to="/create-offer" />} />
                        <Route path="/remote-offer" element={<RemoteOffer />} />
                        <Route path="/create-offer" element={<CreateOffer />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/check-limit" element={<CheckYourLimit />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/offers" element={<Offers />} />
                        <Route path="/offers/:id" element={<OfferDetails />} />
                        <Route path="*" element={<Navigate to="/create-offer" />} />
                    </Route>
                </Route>
            </Routes>
        </Suspense>
    )
}

export default MainAppSLO
